import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Pagination,
  TablePagination,
  TextField,
  Avatar
} from '@mui/material';
import { 
  AdminPanelSettings, 
  Person, 
  Edit, 
  CheckCircle, 
  Cancel,
  Refresh,
  Add,
  Delete
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import axiosInstance from '../api/axiosInstance';

const AdminPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    permissionLevel: '',
    isActive: true
  });

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    permissionLevel: 'viewer'
  });

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await authService.getUsers(page + 1, rowsPerPage);
      setUsers(response.users);
      setTotalUsers(response.pagination.total);
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      permissionLevel: user.permissionLevel,
      isActive: user.isActive
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Update profile information (name and email)
      if (editData.firstName !== selectedUser.firstName || 
          editData.lastName !== selectedUser.lastName || 
          editData.email !== selectedUser.email) {
        await authService.updateUserProfile(selectedUser.id, {
          firstName: editData.firstName,
          lastName: editData.lastName,
          email: editData.email
        });
      }
      
      // Update permission level
      if (editData.permissionLevel !== selectedUser.permissionLevel) {
        await authService.updateUserPermission(selectedUser.id, editData.permissionLevel);
      }
      
      // Update active status
      if (editData.isActive !== selectedUser.isActive) {
        await authService.updateUserStatus(selectedUser.id, editData.isActive);
      }
      
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    } catch (error) {
      setError(error.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditData({
      firstName: '',
      lastName: '',
      email: '',
      permissionLevel: '',
      isActive: true
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axiosInstance.post('/auth/create-privileged-user', newUser, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setNewUser({ username: '', email: '', password: '', firstName: '', lastName: '', permissionLevel: 'viewer' });
        setCreateDialogOpen(false);
        setSuccess('User created successfully!');
        fetchUsers(); // Refresh user list
      } else {
        setError('Error creating user: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Error creating user: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axiosInstance.delete(`/auth/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSuccess('User deleted successfully!');
        fetchUsers(); // Refresh user list
      } else {
        setError('Error deleting user: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error deleting user: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPermissionColor = (level) => {
    switch (level) {
      case 'admin': return 'error';
      case 'editor': return 'warning';
      case 'viewer': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AdminPanelSettings sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Admin Panel
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              color="primary"
            >
              Create User
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchUsers}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Permission Level</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Member Since</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((userData) => (
                    <TableRow key={userData.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {userData.profilePicture ? (
                            <Avatar
                              src={userData.profilePicture}
                              alt={userData.username}
                              sx={{ width: 32, height: 32, mr: 1 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                              {userData.firstName?.[0]}{userData.lastName?.[0]}
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {userData.firstName} {userData.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{userData.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{userData.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={userData.permissionLevel}
                          color={getPermissionColor(userData.permissionLevel)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={userData.isActive ? 'Active' : 'Inactive'}
                          color={userData.isActive ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(userData.lastLogin)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(userData.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => handleEditUser(userData)}
                            disabled={userData.id === user?.id} // Prevent editing own account
                          >
                            Edit
                          </Button>
                          {userData.id !== user?.id && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteUser(userData.id, `${userData.firstName} ${userData.lastName}`)}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit User: {selectedUser?.firstName} {selectedUser?.lastName}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="First Name"
                value={editData.firstName}
                onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Last Name"
                value={editData.lastName}
                onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                required
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Permission Level</InputLabel>
                <Select
                  value={editData.permissionLevel}
                  onChange={(e) => setEditData({ ...editData, permissionLevel: e.target.value })}
                  label="Permission Level"
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={editData.isActive}
                    onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label="Active User"
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUser} 
              variant="contained" 
              startIcon={<CheckCircle />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleCreateUser} sx={{ pt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="First Name"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Last Name"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
              />
              
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Permission Level</InputLabel>
                <Select
                  value={newUser.permissionLevel}
                  onChange={(e) => setNewUser({...newUser, permissionLevel: e.target.value})}
                  label="Permission Level"
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              variant="contained" 
              startIcon={<Add />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminPage;
